import React, { useCallback } from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { Translate } from 'react-localize-redux';
import { CallHistoryMethodAction, goBack, push } from 'connected-react-router';

import styled from 'styled-components';

import SimpleArtist from '@common/types/artist/simple-artist';

import { UPDATE_ARTIST } from '@webapp/store/artist/actions';
import { DeactivateArtist } from '@webapp/store/artist/action-creators';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import ArtistEarningList from '@webapp/controllers/artist/artist-earning-list';

import ArtistInfoAvatar from './artist-info-avatar';
import { ListReleases } from '@webapp/store/release/action-creators';
import { SimpleRelease } from '@common/types/release/simple-release';

interface ArtistDeletePageProps {
  className?: string;
  isLoading: boolean;
  artist: SimpleArtist;
  artistId?: string;
  releases: SimpleRelease[];
  deactivateArtist: (artist: SimpleArtist) => DeactivateArtist;
  goBack: () => CallHistoryMethodAction;
  goTo: (path: string) => CallHistoryMethodAction;
  getReleases: () => ListReleases;
}

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  isLoading: !!state.ui.loading[UPDATE_ARTIST],
  artist: state.artist.active,
  releases: state.release.list.all || [],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goBack: () => dispatch(goBack()),
  goTo: (path: string) => dispatch(push(path)),
  deactivateArtist: (artist: SimpleArtist) =>
    dispatch(new DeactivateArtist(artist)),
  getReleases: () => dispatch(new ListReleases('all')),
});

export const ArtistDeactivatePage: React.FC<ArtistDeletePageProps> = ({
  deactivateArtist,
  artist,
  releases,
  artistId,
  getReleases,
  goTo,
  ...props
}) => {
  React.useEffect(() => {
    if (artistId) {
      getReleases();
    }
  }, [artistId, getReleases]);

  React.useEffect(() => {
    if (artist && (!artist.enabled || artist.isActive === false)) {
      goTo('/user/general');
    }
  }, [artist, goTo]);

  const handleDeactivateArtist = useCallback(() => {
    deactivateArtist(artist);
  }, [deactivateArtist, artist]);

  return (
    <MotionOpacity>
      <div className={props.className}>
        <Typography className="mb-30" type="subheader" customFont="semi-bold">
          <Translate id="ARTIST.DEACTIVATE.TITLE" />
        </Typography>

        <Box noBorderBottom>
          {artist ? (
            <ArtistInfoAvatar
              className="mb-30"
              name={artist.name}
              src={artist.avatar}
              releases={releases.length}
            />
          ) : null}
          <ArtistEarningList />
        </Box>
        <Hint
          noBorderTop
          customTitle={<Translate id="ARTIST.DEACTIVATE.TEXT" />}
        >
          <Translate id="ARTIST.DEACTIVATE.CAPTION" />
        </Hint>

        <Flex gap={5} style={{ width: '100%' }} className="mt-40">
          <Button buttonType="primary" fullWidth onClick={props.goBack}>
            <Translate id="ARTIST.DEACTIVATE.BUTTON.CANCEL" />
          </Button>
          <Button
            buttonType="alert-secondary"
            fullWidth
            onClick={handleDeactivateArtist}
          >
            <Translate id="ARTIST.DEACTIVATE.BUTTON.DEACTIVATE" />
          </Button>
        </Flex>
      </div>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistDeactivatePage)`
  margin-bottom: 100px;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-bottom: 0;
  }
`);
